import React, { useCallback, useEffect, useState } from "react";
import { CalendarClock, Plus, AlertTriangle, ShieldCheck, Play, Pause, Trash2, Send, X, Repeat } from "lucide-react";
import {
  PageHeader, Button, Input, Textarea, Select, Modal, Card, CardContent, StatusBadge,
  LoadingBlock, EmptyState, toast,
} from "@/components/ds";
import { api, formatApiError, type SeasonalConfig, type SeasonalRule, type SeasonalOccurrence, type SeasonalCalendar, type SeasonalMetrics } from "@/lib/api";

const OCC_TONE: Record<string, "slate" | "blue" | "amber" | "green"> = {
  pending_authorization: "amber", authorized: "blue", sending: "blue", completed: "green", cancelled: "slate",
};
const OCC_LABEL: Record<string, string> = {
  pending_authorization: "Needs authorization", authorized: "Authorized", sending: "Sending",
  completed: "Completed", cancelled: "Cancelled",
};

export default function Seasonal() {
  const [config, setConfig] = useState<SeasonalConfig | null>(null);
  const [rules, setRules] = useState<SeasonalRule[] | null>(null);
  const [cal, setCal] = useState<SeasonalCalendar | null>(null);
  const [metrics, setMetrics] = useState<SeasonalMetrics | null>(null);
  const [showRule, setShowRule] = useState(false);
  const [showOcc, setShowOcc] = useState(false);

  const load = useCallback(() => {
    api.seasonalRules().then(setRules).catch((e) => toast.error(formatApiError(e)));
    api.seasonalCalendar().then(setCal).catch(() => {});
    api.seasonalMetrics().then(setMetrics).catch(() => {});
  }, []);
  useEffect(() => { api.seasonalConfig().then(setConfig).catch(() => {}); }, []);
  useEffect(() => { if (config?.included) load(); else { setRules([]); setCal({ occurrences: [], projections: [] }); } }, [config, load]);

  const act = async (p: Promise<unknown>, ok: string) => {
    try { await p; toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const m = metrics;
  const cards = m ? [
    { label: "Campaigns", value: m.campaigns }, { label: "Needs authorization", value: m.pending_authorization },
    { label: "Messages sent", value: m.messages_sent }, { label: "Unavailable", value: m.messages_unavailable },
  ] : [];

  return (
    <div className="space-y-6" data-testid="page-seasonal">
      <PageHeader title="Seasonal Campaigns" description="Keep your business in front of customers all year — templates, scheduling rules and a calendar." testid="seasonal-header"
        action={config?.included ? (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowOcc(true)} data-testid="seasonal-new-campaign-btn"><Plus className="h-4 w-4" /> New campaign</Button>
            {config.recurring && <Button onClick={() => setShowRule(true)} data-testid="seasonal-new-rule-btn"><Repeat className="h-4 w-4" /> Recurring rule</Button>}
          </div>
        ) : undefined} />

      {config && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="seasonal-config">
          <StatusBadge tone="blue">{config.plan} plan</StatusBadge>
          {config.included ? <StatusBadge tone="green">Included · up to {config.max_rules} rules</StatusBadge>
            : <StatusBadge tone="amber">Not included — upgrade to Growth+</StatusBadge>}
          {config.included && (config.recurring ? <StatusBadge tone="green">Recurring automation</StatusBadge> : <StatusBadge tone="slate">Manual only</StatusBadge>)}
          {config.included && (config.sms_configured ? <StatusBadge tone="green">SMS live</StatusBadge>
            : <StatusBadge tone="amber"><AlertTriangle className="mr-1 inline h-3 w-3" /> SMS: configuration required</StatusBadge>)}
        </div>
      )}

      {config && config.included && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3" data-testid="seasonal-authorization-note">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
          <p className="text-xs text-blue-800">Nothing is sent without your authorization. Campaigns are created as <b>“Needs authorization”</b>; recurring rules on Pro/Custom can be authorized once to run automatically. At every send we re-check consent and exclude anyone who opted out or has no valid phone{config.sms_configured ? "." : " — and while SMS is not yet live, recipients are marked “Unavailable — Configuration Required” and never recorded as sent."}</p>
        </div>
      )}

      {config && !config.included && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700" data-testid="seasonal-upgrade">
          Seasonal campaigns are available on Growth and above. Upgrade to plan AC tune-up, winterization and inspection campaigns throughout the year.
        </div>
      )}

      {config?.included && (
        <>
          <div className="grid gap-4 sm:grid-cols-4" data-testid="seasonal-metrics">
            {cards.map((c) => (
              <div key={c.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="text-xs font-medium text-slate-500">{c.label}</div>
                <div className="mt-2 font-display text-2xl font-semibold text-slate-900">{c.value}</div>
              </div>
            ))}
          </div>

          {/* Recurring rules */}
          {config.recurring && (
            <div>
              <h2 className="mb-3 text-sm font-semibold text-slate-800">Recurring automation rules</h2>
              {rules === null ? <LoadingBlock rows={2} /> : rules.length === 0 ? (
                <EmptyState icon={Repeat} title="No recurring rules yet" description="Create an annual rule (e.g. AC tune-up every March) — it generates a campaign each season for your authorization." />
              ) : (
                <div className="space-y-2" data-testid="seasonal-rules">
                  {rules.map((r) => (
                    <Card key={r.id}><CardContent className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`seasonal-rule-${r.id}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-slate-900">{r.name}</p>
                          <StatusBadge tone={r.status === "active" ? "green" : r.status === "paused" ? "amber" : "slate"}>{r.status}</StatusBadge>
                          {r.recurring_authorized ? <StatusBadge tone="green">Authorized</StatusBadge> : <StatusBadge tone="amber">Not authorized</StatusBadge>}
                        </div>
                        <p className="mt-1 text-xs text-slate-500">{r.schedule_type === "annual" ? `Annually on ${r.annual_month}/${r.annual_day}` : `Once on ${r.run_date}`} · generates {r.lead_days}d ahead</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {!r.recurring_authorized && r.recurring && <Button size="sm" onClick={() => act(api.seasonalRuleAuthorize(r.id), "Recurring automation authorized")} data-testid={`seasonal-rule-authorize-${r.id}`}><ShieldCheck className="h-3.5 w-3.5" /> Authorize & activate</Button>}
                        {r.status === "active" && <Button size="sm" variant="outline" onClick={() => act(api.seasonalRulePause(r.id), "Rule paused")} data-testid={`seasonal-rule-pause-${r.id}`}><Pause className="h-3.5 w-3.5" /> Pause</Button>}
                        {r.status === "paused" && <Button size="sm" variant="outline" onClick={() => act(api.seasonalRuleResume(r.id), "Rule resumed")} data-testid={`seasonal-rule-resume-${r.id}`}><Play className="h-3.5 w-3.5" /> Resume</Button>}
                        <Button size="sm" variant="ghost" onClick={() => act(api.seasonalRuleCancel(r.id), "Rule cancelled")} data-testid={`seasonal-rule-cancel-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </CardContent></Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Calendar / occurrences */}
          <div>
            <h2 className="mb-3 text-sm font-semibold text-slate-800">Campaign calendar</h2>
            {cal === null ? <LoadingBlock rows={3} /> : (cal.occurrences.length === 0 && cal.projections.length === 0) ? (
              <EmptyState icon={CalendarClock} title="No campaigns scheduled" description="Create a seasonal campaign or a recurring rule to see it on the calendar." />
            ) : (
              <Card><CardContent className="divide-y divide-slate-100 p-0" data-testid="seasonal-calendar">
                {cal.projections.map((p) => (
                  <div key={p.rule_id + p.scheduled_for} className="flex items-center justify-between px-4 py-3" data-testid={`seasonal-projection-${p.rule_id}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-slate-500">{p.scheduled_for}</span>
                      <span className="text-sm text-slate-700">{p.name}</span>
                    </div>
                    <StatusBadge tone="slate">Upcoming · {p.authorized ? "auto" : "needs auth"}</StatusBadge>
                  </div>
                ))}
                {cal.occurrences.map((o) => (
                  <div key={o.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`seasonal-occ-${o.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-slate-500">{o.scheduled_for}</span>
                        <span className="text-sm text-slate-800">{o.name}</span>
                        <StatusBadge tone={OCC_TONE[o.status] ?? "slate"}>{OCC_LABEL[o.status] ?? o.status}</StatusBadge>
                      </div>
                      {o.status === "completed" && (
                        <div className="mt-1 flex gap-3 text-xs text-slate-500" data-testid={`seasonal-occ-stats-${o.id}`}>
                          <span>Sent <b className="text-emerald-700">{o.stats.sent}</b></span>
                          <span>Unavailable <b className="text-amber-700">{o.stats.unavailable}</b></span>
                          <span>Excluded <b className="text-slate-600">{(o.audience_snapshot?.excluded as number) ?? 0}</b></span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {o.status === "pending_authorization" && <Button size="sm" onClick={() => act(api.seasonalCampaignAuthorize(o.id), "Campaign authorized & scheduled")} data-testid={`seasonal-occ-authorize-${o.id}`}><ShieldCheck className="h-3.5 w-3.5" /> Authorize & schedule</Button>}
                      {o.status === "authorized" && <Button size="sm" variant="outline" onClick={() => act(api.seasonalCampaignSend(o.id), "Sending campaign")} data-testid={`seasonal-occ-send-${o.id}`}><Send className="h-3.5 w-3.5" /> Send now</Button>}
                      {["pending_authorization", "authorized"].includes(o.status) && <Button size="sm" variant="ghost" onClick={() => act(api.seasonalCampaignCancel(o.id), "Campaign cancelled")} data-testid={`seasonal-occ-cancel-${o.id}`}><X className="h-3.5 w-3.5" /></Button>}
                    </div>
                  </div>
                ))}
              </CardContent></Card>
            )}
          </div>
        </>
      )}

      {showRule && config && <RuleModal config={config} onClose={() => setShowRule(false)} onSaved={load} />}
      {showOcc && config && <OccurrenceModal config={config} onClose={() => setShowOcc(false)} onSaved={load} />}
    </div>
  );
}

function usePresetPicker(config: SeasonalConfig, setName: (s: string) => void, setMessage: (s: string) => void, setSeason: (s: string) => void) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-slate-500">Start from a preset</p>
      <div className="flex flex-wrap gap-2">
        {config.presets.map((p) => (
          <button key={p.season_key} type="button" onClick={() => { setName(p.name); setMessage(p.message); setSeason(p.season_key); }}
            className="rounded-full border border-slate-200 px-3 py-1 text-xs hover:border-blue-400 hover:bg-blue-50" data-testid={`seasonal-preset-${p.season_key}`}>{p.name}</button>
        ))}
      </div>
    </div>
  );
}

function RuleModal({ config, onClose, onSaved }: { config: SeasonalConfig; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [season, setSeason] = useState("custom");
  const [month, setMonth] = useState("3");
  const [day, setDay] = useState("15");
  const [audienceType, setAudienceType] = useState("dynamic");
  const [busy, setBusy] = useState(false);
  const preset = usePresetPicker(config, setName, setMessage, setSeason);

  const save = async () => {
    if (!name || !message) { toast.error("Add a name and message"); return; }
    setBusy(true);
    try {
      await api.seasonalRuleCreate({ name, message, schedule_type: "annual", annual_month: Number(month), annual_day: Number(day), audience: { type: audienceType } });
      toast.success("Recurring rule created — authorize it to activate"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const audOpts = [{ value: "dynamic", label: "All eligible contacts (re-checked each send)" }, ...(config.advanced_segments ? [{ value: "segment", label: "Advanced segment (Custom)" }] : [])];

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="New recurring seasonal rule" description="Generates a campaign each year for your authorization. It never sends automatically until you authorize it." testid="seasonal-rule-modal">
      <div className="max-h-[74vh] space-y-4 overflow-y-auto pr-1">
        {preset}
        <div><p className="mb-1 text-xs font-medium text-slate-500">Rule name</p><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="seasonal-rule-name" /></div>
        <div><p className="mb-1 text-xs font-medium text-slate-500">Message <span className="text-slate-400">(use {"{first_name}"}, {"{business}"})</span></p>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} data-testid="seasonal-rule-message" /></div>
        <div className="flex gap-3">
          <div className="w-28"><p className="mb-1 text-xs font-medium text-slate-500">Month</p><Input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="seasonal-rule-month" /></div>
          <div className="w-28"><p className="mb-1 text-xs font-medium text-slate-500">Day</p><Input type="number" min="1" max="31" value={day} onChange={(e) => setDay(e.target.value)} data-testid="seasonal-rule-day" /></div>
        </div>
        <div><p className="mb-1 text-xs font-medium text-slate-500">Audience</p><Select value={audienceType} onChange={setAudienceType} options={audOpts} testid="seasonal-rule-audience" /></div>
      </div>
      <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={busy} data-testid="seasonal-rule-save-btn">Create rule</Button>
      </div>
    </Modal>
  );
}

function OccurrenceModal({ config, onClose, onSaved }: { config: SeasonalConfig; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [season, setSeason] = useState("custom");
  const [date, setDate] = useState("");
  const [audienceType, setAudienceType] = useState("dynamic");
  const [busy, setBusy] = useState(false);
  const preset = usePresetPicker(config, setName, setMessage, setSeason);

  const save = async () => {
    if (!name || !message || !date) { toast.error("Add a name, message and send date"); return; }
    setBusy(true);
    try {
      await api.seasonalCampaignCreate({ name, message, scheduled_for: date, season_key: season, audience: { type: audienceType } });
      toast.success("Campaign created — authorize it to schedule the send"); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const audOpts = [{ value: "dynamic", label: "All eligible contacts (re-checked at send)" }, ...(config.advanced_segments ? [{ value: "segment", label: "Advanced segment (Custom)" }] : [])];

  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title="New seasonal campaign" description="Created as ‘Needs authorization’. It only sends after you authorize it, and consent is re-checked at send time." testid="seasonal-occ-modal">
      <div className="max-h-[74vh] space-y-4 overflow-y-auto pr-1">
        {preset}
        <div><p className="mb-1 text-xs font-medium text-slate-500">Campaign name</p><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="seasonal-occ-name" /></div>
        <div><p className="mb-1 text-xs font-medium text-slate-500">Message <span className="text-slate-400">(use {"{first_name}"}, {"{business}"})</span></p>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} data-testid="seasonal-occ-message" />
          <p className="mt-1 text-[11px] text-slate-400">An opt-out line (Reply STOP) is included; opted-out and no-phone contacts are automatically excluded.</p></div>
        <div><p className="mb-1 text-xs font-medium text-slate-500">Send date</p><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="seasonal-occ-date" /></div>
        <div><p className="mb-1 text-xs font-medium text-slate-500">Audience</p><Select value={audienceType} onChange={setAudienceType} options={audOpts} testid="seasonal-occ-audience" /></div>
      </div>
      <div className="mt-4 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={busy} data-testid="seasonal-occ-save-btn">Create campaign</Button>
      </div>
    </Modal>
  );
}
